<script lang="ts">
  import { formatMoney } from '$lib/format';
  import { t } from '$lib/i18n/index.svelte';
  import { defang } from '$lib/protocol/link-guard';
  import type { CartElement } from '$lib/protocol/types';

  import type { ElementContext } from './context';

  let { element, ctx }: { element: CartElement; ctx: ElementContext } = $props();

  // Working quantities: user edits layered over the proposed values. The client
  // sends quantities and a decision only — never prices; everything with
  // monetary value comes from the seller.
  let edited = $state<Record<string, number>>({});
  let note = $state('');

  const quantities = $derived.by(() => {
    const out: Record<string, number> = {};
    for (const item of element.items) {
      out[item.itemId] = edited[item.itemId] ?? item.quantity;
    }
    return out;
  });

  const stale = $derived(ctx.staleCarts.has(element.cartId) || element.state === 'superseded');
  const awaiting = $derived(element.state === 'proposed' && !stale);
  const interactive = $derived(awaiting && !ctx.disabled);
  const changed = $derived(
    element.items.some((item) => (quantities[item.itemId] ?? item.quantity) !== item.quantity),
  );

  function bump(itemId: string, delta: number): void {
    const current = quantities[itemId] ?? 0;
    edited = { ...edited, [itemId]: Math.max(0, current + delta) };
  }

  function respond(decision: 'accept' | 'amend' | 'decline'): void {
    const items =
      decision === 'amend'
        ? element.items.map((item) => ({
            itemId: item.itemId,
            quantity: quantities[item.itemId] ?? item.quantity,
          }))
        : [];
    ctx.onCartRespond(element.cartId, decision, items, note.trim().length > 0 ? note.trim() : undefined);
  }

  function stateLabel(state: string): string {
    switch (state) {
      case 'proposed':
        return t('el.cart.state.proposed');
      case 'accepted':
        return t('el.cart.state.accepted');
      case 'declined':
        return t('el.cart.state.declined');
      case 'amended':
        return t('el.cart.state.amended');
      default:
        return t('el.cart.state.superseded');
    }
  }
</script>

<div class="cart" class:stale>
  <div class="head">
    <strong>{t('el.cart.title')}</strong>
    <span class="state">{stale ? t('el.cart.state.superseded') : stateLabel(element.state)}</span>
  </div>

  <ul>
    {#each element.items as item (item.itemId)}
      <li>
        <div class="line">
          <span class="name">{defang(item.name)}</span>
          {#if item.unitPrice !== undefined}
            <span class="price">{formatMoney(item.unitPrice)}</span>
          {/if}
        </div>
        <div class="line sub">
          {#if interactive}
            <span class="stepper">
              <button onclick={() => bump(item.itemId, -1)} aria-label="-">−</button>
              <span class="qty">{quantities[item.itemId] ?? item.quantity}{item.unit !== undefined ? ` ${item.unit}` : ''}</span>
              <button onclick={() => bump(item.itemId, 1)} aria-label="+">+</button>
            </span>
          {:else}
            <span class="qty">{item.quantity}{item.unit !== undefined ? ` ${item.unit}` : ''}</span>
          {/if}
          {#if item.total !== undefined}
            <span class="price">{formatMoney(item.total)}</span>
          {/if}
        </div>
        {#if item.note !== undefined}
          <p class="note">{defang(item.note)}</p>
        {/if}
      </li>
    {/each}
  </ul>

  {#if element.total !== undefined}
    <div class="total">
      <span>{t('el.cart.total')}</span>
      <strong>{formatMoney(element.total)}</strong>
    </div>
  {/if}

  {#if interactive}
    <input class="notefield" placeholder={t('el.cart.note')} bind:value={note} />
    <div class="actions">
      <button class="decline" onclick={() => respond('decline')}>{t('el.cart.decline')}</button>
      {#if changed}
        <button class="accept" onclick={() => respond('amend')}>{t('el.cart.adjust')}</button>
      {:else}
        <button class="accept" onclick={() => respond('accept')}>{t('el.cart.accept')}</button>
      {/if}
    </div>
  {/if}
</div>

<style>
  .cart {
    min-width: 240px;
  }
  .stale {
    opacity: 0.65;
  }
  .head {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 6px;
  }
  .state {
    font-size: 12px;
    color: var(--ink-soft);
  }
  ul {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  li {
    border-top: 1px solid var(--line);
    padding-top: 8px;
  }
  .line {
    display: flex;
    justify-content: space-between;
    gap: 12px;
  }
  .sub {
    color: var(--ink-soft);
    font-size: 14px;
    margin-top: 2px;
    align-items: center;
  }
  .name {
    overflow-wrap: anywhere;
  }
  .stepper {
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }
  .stepper button {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    border: 1px solid var(--line);
    background: var(--surface);
    color: var(--ink);
    cursor: pointer;
    font-size: 16px;
    line-height: 1;
  }
  .note {
    margin: 4px 0 0;
    font-size: 13px;
    color: var(--ink-soft);
  }
  .notefield {
    width: 100%;
    margin-top: 8px;
    border: 1px solid var(--line);
    border-radius: 8px;
    padding: 8px 10px;
    font: inherit;
    background: var(--surface);
    color: var(--ink);
    box-sizing: border-box;
  }
  .total {
    display: flex;
    justify-content: space-between;
    border-top: 1px solid var(--line);
    margin-top: 8px;
    padding-top: 8px;
  }
  .actions {
    display: flex;
    gap: 8px;
    margin-top: 10px;
    justify-content: flex-end;
  }
  .accept {
    border: 0;
    border-radius: 999px;
    background: var(--brand);
    color: var(--brand-ink);
    padding: 10px 20px;
    font-weight: 600;
    cursor: pointer;
  }
  .decline {
    border: 1px solid var(--line);
    border-radius: 999px;
    background: transparent;
    color: var(--danger);
    padding: 10px 16px;
    cursor: pointer;
  }
</style>
